import {
  Box,
  CircularProgress,
  Icon,
  Link,
  NotificationCard,
  Stack,
  Typography,
  Visible,
} from '@ace-clublabs/shared-component-library';
import { useGlobalState } from '../../state/state';
import { MSSNotification } from '@lib/types';
import { useEffect, useRef, useState } from 'react';
import { GreenBoxMessage } from '../GreenBox/GreenBoxMessage';
import { GreenBoxModal } from '../GreenBox/GreenBoxModal';
export const Notifications = () => {
  const [notifications] = useGlobalState('notifications');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 769);
  const [cardNumber, setCardNumber] = useState(1);
  const [content] = useGlobalState('content');
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const cardRef = useRef(null);
  const numberOfNotifications = notifications?.length ?? 1;

  useEffect(() => {
    const handleScroll = () => {
      if (cardRef.current) {
        const { scrollLeft, scrollWidth } = cardRef.current;
        const cardWidth = scrollWidth / numberOfNotifications;

        setCardNumber(Math.min(Math.ceil(scrollLeft / cardWidth + 1), numberOfNotifications));
      }
    };

    const card = cardRef.current as unknown as HTMLElement;
    if (card) {
      card.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (card) {
        card.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (notifications && notifications.length > 0) ||
    (content?.greenBoxMessage && content?.greenBoxMessage?.shortBoxMessage) ? (
    <Stack sx={{ marginBottom: '2rem', maxWidth: '100vw' }}>
      <Stack direction="column" alignItems="baseline" justifyContent="space-between">
        <Typography sx={{ marginBottom: '1rem' }} variant="h5" className="notification-title">
          Notifications
        </Typography>
        {content.greenBoxMessage && (
          <Typography
            sx={{
              marginBottom: '1rem',
              width: '100%',
            }}
          >
            <GreenBoxMessage
              shortBoxMessage={content.greenBoxMessage.shortBoxMessage}
              modalTitle={content.greenBoxMessage.modalTitle}
              modalBody={content.greenBoxMessage.modalBody}
            />
          </Typography>
        )}
        {numberOfNotifications > 1 && (
          <Visible predicate={{ xs: true, sm: true, md: false, lg: false }}>
            <Typography variant="b2Regular">
              {cardNumber} of {numberOfNotifications}
            </Typography>
          </Visible>
        )}
      </Stack>
      <Stack
        ref={cardRef}
        alignItems="stretch"
        gap="0.5rem"
        flexDirection={isMobile ? 'row' : 'column'}
        sx={{
          overflowX: 'scroll',
          height: '100%',
          margin: '0 -1.5rem',
          padding: '0 1.5rem',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {getNotificationCards(notifications)}
      </Stack>
    </Stack>
  ) : (
    <></>
  );
};

export const Notification = (props: MSSNotification) => {
  const [showGreenBoxModal, setShowGreenBoxModal] = useState(false);
  const { type, message, link, boldText, onClick, iconOverride, modalTitle, modalBody } = props;
  const [loading, setLoading] = useState(false);

  const getOnClick = () => {
    if (onClick && !loading) {
      return async () => {
        setLoading(true);
        await onClick();
        setLoading(false);
      };
    } else if (link) {
      return () => window.location.assign(link);
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 769);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <NotificationCard
      onClick={getOnClick()}
      severity={type}
      sx={{
        maxWidth: '554px',
        minWidth: isMobile ? '332px' : undefined,
        opacity: loading ? '80%' : undefined,
        '& .MuiPaper-root': {
          height: '100%',
          alignItems: 'center',
        },
        width: '100%',
      }}
      slotProps={{
        alert: {
          icon: iconOverride ? <Icon iconName={iconOverride as any} /> : undefined,
          ...(loading
            ? {
                action: (
                  <Box sx={{ height: '1.5rem !important', marginRight: '0.25rem' }}>
                    <CircularProgress size={24} />
                  </Box>
                ),
              }
            : {}),
        },
      }}
    >
      <Typography className={`${type.toLowerCase()}-notification`} variant="b2Regular">
        <Stack className="message-container" direction="row">
          <Box>
            {boldText ? <strong>{boldText} </strong> : <></>}
            {message}
            {modalTitle && (
              <>
                {' '}
                <Link
                  onClick={() => setShowGreenBoxModal(true)}
                  sx={{ color: '#0156A3', fontSize: '0.875rem !important', cursor: 'pointer' }}
                  className="learn-more-link"
                >
                  Learn more
                </Link>
              </>
            )}
          </Box>
        </Stack>
      </Typography>
      <GreenBoxModal
        showGreenBoxModal={showGreenBoxModal}
        modalTitle={modalTitle}
        modalContent={modalBody}
        closeModal={() => {
          setShowGreenBoxModal(false);
        }}
      />
    </NotificationCard>
  );
};

const getNotificationCards = (notifications?: MSSNotification[]) =>
  notifications?.map((n, i) => <Notification {...n} key={i} />);
